import { Alert, Button, Group, Modal, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useCallback, useMemo, type FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { z } from "zod";
import { useTRPC } from "../../../../trpc";
import { assertAccountSelected, useSetUser, useUser } from "../../../../hooks/user";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useNavigate } from "@tanstack/react-router";
import { IconAlertCircle } from "@tabler/icons-react";

interface ChangeAccountModalProps {
  open: boolean;
  onClose: () => void;
}
interface FormValuesRename {
  [key: string]: string;
  displayName: string;
}

export const ChangeAccountModal: FC<ChangeAccountModalProps> = ({ open, onClose }) => {
  const intl = useIntl();
  const user = useUser();
  const trpc = useTRPC();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setUser = useSetUser();
  assertAccountSelected(user);

  const { mutate: changeAccountName, isPending } = useMutation(trpc.updateCurrentAccount.mutationOptions());
  const { refetch } = useQuery(trpc.getCurrentUser.queryOptions(undefined, {
    enabled: false,
  }));
  const validate = useMemo(() => zod4Resolver(z.object({
    displayName: z.string().min(3, {
      message: intl.formatMessage({ defaultMessage: "Name should have at least 3 characters", id: "error.nameTooShort3Chars" }),
    }),
  })), [intl]);

  const { onSubmit, getInputProps, setInitialValues } = useForm({
    validateInputOnBlur: true,
    initialValues: {
      displayName: user.currentAccount.displayName,
    },
    validate,
  });

  const handleSubmit = useCallback((form: FormValuesRename) => {
    changeAccountName(form, {
      onSuccess: (updatedAccount) => {
        void queryClient.setQueryData(trpc.getCurrentAccount.queryKey(), updatedAccount);
        void refetch().then(({ data }) => {
          if (data?.currentAccount) {
            setUser(data);
            onClose();
            setInitialValues({
              displayName: data.currentAccount.displayName,
            });
            void navigate({
              to: "/$accountName/admin/configurations",
              params: { accountName: data.currentAccount.name },
            });
          }
        });
      },
    });
  }, [changeAccountName, onClose, setUser]);

  return (
    <Modal opened={open} onClose={onClose} title={intl.formatMessage({ defaultMessage: "Change account name", id: "title.changeAccountName" })}>
      <form onSubmit={onSubmit(handleSubmit)}>
        <Alert variant="light" color="yellow" icon={<IconAlertCircle />}>
          <FormattedMessage
            defaultMessage="Changing account name will change all links and shared content like maps, tilesets, and datasets."
            id="text.changeAccountNameDescription"
          />
        </Alert>
        <TextInput
          data-autofocus
          disabled={isPending}
          label={intl.formatMessage({ defaultMessage: "Account name", id: "label.accountName" })}
          {...getInputProps("displayName")}
        />

        <Group gap="xs" justify="flex-end" pt="lg">
          <Button variant="secondary" type="reset" loading={isPending} onClick={onClose}>
            <FormattedMessage defaultMessage="Cancel" id="button.cancel" />
          </Button>

          <Button variant="tertiary" type="submit" loading={isPending}>
            <FormattedMessage defaultMessage="Save" id="button.save" />
          </Button>
        </Group>
      </form>
    </Modal>
  );
};
